import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Blocks, FileText } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { getApiErrorPresentation } from '../utils/apiError';

function DokumenChunkVerificationPage() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [document, setDocument] = useState(null);
  const [content, setContent] = useState('');
  const [savedContent, setSavedContent] = useState('');
  const [chunks, setChunks] = useState([]);
  const [retrievalData, setRetrievalData] = useState(null);
  const [retrievalSearch, setRetrievalSearch] = useState('');
  const [retrievalPage, setRetrievalPage] = useState(1);
  const [activeChunk, setActiveChunk] = useState(0);
  const [showChunkModal, setShowChunkModal] = useState(false);
  const [showOcrPreviewModal, setShowOcrPreviewModal] = useState(false);
  const [chunkPage, setChunkPage] = useState(1);
  const [chunkSearch, setChunkSearch] = useState('');
  const [ocrPreview, setOcrPreview] = useState(null);
  const [ocrPreviewLoading, setOcrPreviewLoading] = useState(false);
  const [ocrPreviewError, setOcrPreviewError] = useState('');
  const [wrapEditor, setWrapEditor] = useState(true);
  const previewRequestRef = useRef(0);
  const contentTextareaRef = useRef(null);
  const lineNumbersRef = useRef(null);
  const highlightRef = useRef(null);

  // Line height constant (font 14px * 1.5 = 21px) — must match textarea style
  const LINE_H = 21;
  const SHOW_CHUNK_DATA = false;

  const handleEditorScroll = (e) => {
    const { scrollTop, scrollLeft } = e.target;
    if (lineNumbersRef.current) {
      lineNumbersRef.current.scrollTop = scrollTop;
    }
    if (highlightRef.current) {
      highlightRef.current.scrollTop = scrollTop;
      highlightRef.current.scrollLeft = scrollLeft;
    }
  };

  const renderLineNumbers = () => {
    const lines = content ? content.split('\n') : [''];
    return lines.map((line, i) => {
      const isSep = line.includes('[CHUNK_SEPARATOR]');
      return (
        <div
          key={i}
          style={{
            height: `${LINE_H}px`,
            lineHeight: `${LINE_H}px`,
            paddingRight: '8px',
            paddingLeft: '4px',
            textAlign: 'right',
            backgroundColor: isSep ? '#0d6efd' : 'transparent',
            color: isSep ? '#fff' : '#6c757d',
            fontWeight: isSep ? 'bold' : 'normal',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          {String(i + 1).padStart(3, '0')}
        </div>
      );
    });
  };

  const renderSeparatorHighlights = () => {
    const lines = content ? content.split('\n') : [''];
    return lines.map((line, i) => {
      const isSep = line.includes('[CHUNK_SEPARATOR]');
      return (
        <div
          key={i}
          style={{
            height: `${LINE_H}px`,
            backgroundColor: isSep ? 'rgba(13, 110, 253, 0.12)' : 'transparent',
            borderLeft: isSep ? '3px solid #0d6efd' : '3px solid transparent',
          }}
        />
      );
    });
  };

  const fetchVerificationData = async () => {
    setLoading(true);
    setOcrPreview(null);
    setOcrPreviewError('');
    try {
      const [response, retrievalResponse] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/admin/rag-documents/${documentId}/verification`,
          { withCredentials: true }
        ),
        axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/admin/rag-documents/${documentId}/retrieval-chunks`,
          { withCredentials: true }
        ),
      ]);

      setDocument(response.data.document);
      setRetrievalData(retrievalResponse.data);
      setRetrievalPage(1);
      const loadedContent = response.data.document?.content || '';
      setContent(loadedContent);
      setSavedContent(loadedContent);
      setChunks((response.data.chunks || []).map((chunk) => ({
        ...chunk,
        verified_text: chunk.verified_text || chunk.chunk_text || '',
      })));
      setActiveChunk(0);
    } catch (error) {
      Swal.fire('Oops!', error.response?.data?.message || 'Gagal memuat chunk dokumen.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchOcrPreview = async (text) => {
    const normalizedText = (text || '').trim();
    if (!normalizedText) {
      setOcrPreview(null);
      setOcrPreviewError('');
      setOcrPreviewLoading(false);
      return;
    }

    const requestId = previewRequestRef.current + 1;
    previewRequestRef.current = requestId;
    setOcrPreviewLoading(true);
    setOcrPreviewError('');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/ocr/normalize`,
        {
          text: normalizedText,
          mode: 'safe',
        },
        { withCredentials: true }
      );

      if (previewRequestRef.current !== requestId) return;
      setOcrPreview(response.data);
    } catch (error) {
      if (previewRequestRef.current !== requestId) return;
      setOcrPreview(null);
      setOcrPreviewError(error.response?.data?.message || 'Gagal memuat preview normalisasi OCR.');
    } finally {
      if (previewRequestRef.current === requestId) {
        setOcrPreviewLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchVerificationData();
  }, [documentId]);

  useEffect(() => {
    if (loading || !document) return undefined;

    const timer = setTimeout(() => {
      fetchOcrPreview(content);
    }, 650);

    return () => clearTimeout(timer);
  }, [content, document, loading]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (saving || syncing) {
        e.preventDefault();
        e.returnValue = 'Proses sedang berjalan. Apakah Anda yakin ingin keluar?';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [saving, syncing]);

  const getVerificationBadge = (item) => {
    const status = (item?.verification_status || '').toLowerCase();
    if (status === 'synced') return <span className="badge bg-success">QDRANT SYNCED</span>;
    if (status === 'verified') return <span className="badge bg-info text-dark">VERIFIED</span>;
    if (status === 'needs_review') return <span className="badge bg-warning text-dark">NEED REVIEW</span>;
    return <span className="badge bg-secondary">DRAFT</span>;
  };

  const buildSeparatorText = (chunk) => {
    const validity = chunk?.masih_berlaku === false ? 'false' : 'true';
    if (chunk?.separator_text) {
      if (/MASIH_BERLAKU\s*:/i.test(chunk.separator_text)) {
        return chunk.separator_text.replace(
          /MASIH_BERLAKU\s*:\s*[^|]+?(?=\s*\||\s*---)/i,
          `MASIH_BERLAKU: ${validity}`
        );
      }
      return chunk.separator_text.replace(/\s*---$/, ` | MASIH_BERLAKU: ${validity} ---`);
    }

    const structure = chunk?.structure || {};
    const chunkType = chunk?.chunk_type || structure.chunk_type || '-';
    const parts = [`TIPE: ${chunkType}`];

    if (structure.bab) parts.push(`BAB: ${structure.bab}`);
    if (structure.judul_bab || structure.bab_title) parts.push(`JUDUL_BAB: ${structure.judul_bab || structure.bab_title}`);
    if (structure.bagian) parts.push(`BAGIAN: ${structure.bagian}`);
    if (structure.paragraf) parts.push(`PARAGRAF: ${structure.paragraf}`);
    if (structure.pasal) parts.push(`PASAL: ${structure.pasal}`);
    parts.push(`MASIH_BERLAKU: ${validity}`);

    return `--- [CHUNK_SEPARATOR] ${parts.join(' | ')} ---`;
  };

  const insertSeparatorIntoContent = () => {
    const chunk = chunks[activeChunk];
    if (!chunk) return;

    const separatorText = buildSeparatorText(chunk);
    const textarea = contentTextareaRef.current;

    if (!textarea) {
      setContent((prev) => `${prev.trim()}\n\n${separatorText}\n`.trim());
      return;
    }

    // Save scroll and cursor BEFORE any state change
    const savedScrollTop = textarea.scrollTop;
    const savedScrollLeft = textarea.scrollLeft;
    const start = textarea.selectionStart || 0;
    const end = textarea.selectionEnd || start;
    const currentValue = content || '';
    const prefix = currentValue.slice(0, start);
    const suffix = currentValue.slice(end);
    const leadingBreak = prefix && !prefix.endsWith('\n') ? '\n' : '';
    const trailingBreak = suffix && !suffix.startsWith('\n') ? '\n' : '';
    const insertion = `${leadingBreak}${separatorText}${trailingBreak}`;
    const nextValue = `${prefix}${insertion}${suffix}`;
    const newCursor = prefix.length + insertion.length;

    setContent(nextValue);

    // Restore cursor + scroll after React re-render
    window.requestAnimationFrame(() => {
      const ta = contentTextareaRef.current;
      if (!ta) return;
      ta.focus();
      ta.setSelectionRange(newCursor, newCursor);
      ta.scrollTop = savedScrollTop;
      ta.scrollLeft = savedScrollLeft;
      // sync gutter
      if (lineNumbersRef.current) lineNumbersRef.current.scrollTop = savedScrollTop;
      if (highlightRef.current) {
        highlightRef.current.scrollTop = savedScrollTop;
        highlightRef.current.scrollLeft = savedScrollLeft;
      }
    });
  };

  const openChunkModal = (index) => {
    setActiveChunk(index);
    setShowChunkModal(true);
  };

  const updateChunkVerifiedText = (index, value) => {
    setChunks((currentChunks) => currentChunks.map((chunk, chunkIndex) => (
      chunkIndex === index ? { ...chunk, verified_text: value } : chunk
    )));
  };

  const updateChunkValidity = (index, value) => {
    setChunks((currentChunks) => currentChunks.map((chunk, chunkIndex) => (
      chunkIndex === index ? { ...chunk, masih_berlaku: value } : chunk
    )));
  };

  const saveVerification = async ({ rebuildChunks = false } = {}) => {
    if (!document) return;

    const contentChanged = content !== savedContent;
    const shouldRebuildChunks = rebuildChunks || contentChanged;
    const confirmTitle = shouldRebuildChunks ? 'Buat ulang dan simpan chunk?' : 'Simpan verifikasi chunk?';
    const confirmMsg = shouldRebuildChunks
      ? 'Teks utama berubah. Chunk akan dibuat ulang dari teks tersebut agar perubahan benar-benar tersimpan.'
      : 'Perubahan isi chunk terverifikasi akan disimpan ke database.';

    const result = await Swal.fire({
      title: confirmTitle,
      text: confirmMsg,
      icon: shouldRebuildChunks ? 'warning' : 'question',
      showCancelButton: true,
      confirmButtonText: 'Ya',
      cancelButtonText: 'Batal',
    });

    if (!result.isConfirmed) return;

    setSaving(true);
    Swal.fire({
      title: shouldRebuildChunks ? 'Membuat ulang chunk...' : 'Menyimpan verifikasi...',
      text: 'Mohon tunggu, proses sedang berlangsung.',
      allowOutsideClick: false,
      allowEscapeKey: false,
      allowEnterKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/admin/rag-documents/${document.id}/verification`,
        {
          content,
          chunks: chunks.map((chunk) => ({
            id: chunk.id,
            chunk_type: chunk.chunk_type,
            verified_text: chunk.verified_text ?? chunk.chunk_text ?? '',
            masih_berlaku: chunk.masih_berlaku !== false,
            structure: {
              ...(chunk.structure || {}),
              structural_scope: chunk.structure?.structural_scope ?? chunk.metadata?.structural_scope ?? null,
            },
          })),
          rebuild_chunks: shouldRebuildChunks,
        },
        {
          withCredentials: true,
          timeout: shouldRebuildChunks ? 120000 : 30000,
        }
      );

      setDocument(response.data.document);
      const savedResponseContent = response.data.document?.content || content;
      setContent(savedResponseContent);
      setSavedContent(savedResponseContent);
      setChunks((response.data.chunks || []).map((chunk) => ({
        ...chunk,
        verified_text: chunk.verified_text || chunk.chunk_text || '',
      })));
      try {
        const retrievalResponse = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/admin/rag-documents/${document.id}/retrieval-chunks`,
          { withCredentials: true }
        );
        setRetrievalData(retrievalResponse.data);
        setRetrievalPage(1);
      } catch (_retrievalError) {
        // Save canonical sudah berhasil; kegagalan refresh audit tidak boleh
        // dilaporkan sebagai kegagalan penyimpanan.
      }
      setActiveChunk(0);
      Swal.fire('Berhasil', 'Perubahan verifikasi berhasil disimpan ke database.', 'success');
    } catch (error) {
      Swal.fire('Oops!', error.response?.data?.message || 'Gagal menyimpan verifikasi.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const sendToQdrant = async () => {
    if (!document) return;

    const result = await Swal.fire({
      title: 'Kirim chunk ke Qdrant?',
      text: 'Pastikan struktur BAB/Pasal dan isi pasal sudah diverifikasi. Proses ini akan mengganti point Qdrant lama untuk dokumen ini.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Kirim ke Qdrant',
      cancelButtonText: 'Batal',
    });

    if (!result.isConfirmed) return;

    setSyncing(true);
    Swal.fire({
      title: 'Mengirim ke Qdrant...',
      text: 'Mohon tunggu, proses sinkronisasi sedang berlangsung.',
      allowOutsideClick: false,
      allowEscapeKey: false,
      allowEnterKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/admin/rag-documents/${document.id}/qdrant`,
        {},
        { withCredentials: true }
      );
      await fetchVerificationData();
      Swal.fire('Berhasil', response.data.message || 'Dokumen berhasil dikirim ke Qdrant.', 'success');
    } catch (error) {
      const apiError = getApiErrorPresentation(error, 'Gagal mengirim ke Qdrant.');
      Swal.fire({ ...apiError, icon: 'error' });
    } finally {
      setSyncing(false);
    }
  };

  const filteredChunks = chunks
    .map((chunk, index) => ({ chunk, index }))
    .filter(({ chunk }) => {
      const keyword = chunkSearch.trim().toLowerCase();
      if (!keyword) return true;
      const structure = chunk.structure || {};
      return [
        structure.bab,
        structure.pasal ? `pasal ${structure.pasal}` : '',
        chunk.verified_text,
        chunk.chunk_text,
      ].join(' ').toLowerCase().includes(keyword);
    });

  const CHUNKS_PER_PAGE = 10;
  const totalChunkPages = Math.max(1, Math.ceil(filteredChunks.length / CHUNKS_PER_PAGE));
  const safeChunkPage = Math.min(chunkPage, totalChunkPages);
  const paginatedChunks = filteredChunks.slice(
    (safeChunkPage - 1) * CHUNKS_PER_PAGE,
    safeChunkPage * CHUNKS_PER_PAGE
  );
  const chunkPageItems = Array.from({ length: totalChunkPages }, (_, index) => index + 1)
    .filter((page) => (
      page === 1 ||
      page === totalChunkPages ||
      Math.abs(page - safeChunkPage) <= 1
    ))
    .reduce((items, page, index, pages) => {
      if (index > 0 && page - pages[index - 1] > 1) items.push(`ellipsis-${page}`);
      items.push(page);
      return items;
    }, []);

  const selectedChunk = chunks[activeChunk];
  const retrievalChunks = retrievalData?.chunks || [];
  const filteredRetrievalChunks = retrievalChunks.filter((chunk) => {
    const keyword = retrievalSearch.trim().toLowerCase();
    if (!keyword) return true;
    return [
      chunk.retrieval_identity,
      chunk.source_legal_identity,
      chunk.section_type,
      chunk.chunk_level,
      chunk.bab,
      chunk.bab_title,
      chunk.pasal ? `pasal ${chunk.pasal}` : '',
      chunk.ayat ? `ayat ${chunk.ayat}` : '',
      chunk.huruf ? `huruf ${chunk.huruf}` : '',
      chunk.angka ? `angka ${chunk.angka}` : '',
      chunk.content,
      chunk.parent_context,
    ].join(' ').toLowerCase().includes(keyword);
  });
  const RETRIEVAL_PER_PAGE = 15;
  const retrievalTotalPages = Math.max(1, Math.ceil(filteredRetrievalChunks.length / RETRIEVAL_PER_PAGE));
  const safeRetrievalPage = Math.min(retrievalPage, retrievalTotalPages);
  const paginatedRetrievalChunks = filteredRetrievalChunks.slice(
    (safeRetrievalPage - 1) * RETRIEVAL_PER_PAGE,
    safeRetrievalPage * RETRIEVAL_PER_PAGE
  );

  const getChunkLabel = (chunk, index) => {
    const structure = chunk?.structure || {};
    if (structure.pasal) return `Pasal ${structure.pasal}`;
    if (structure.bab) return structure.bab;
    return `Chunk ${index + 1}`;
  };

  const getChunkBab = (chunk) => {
    const structure = chunk?.structure || {};
    if (structure.bab_title) return `${structure.bab || 'BAB'} - ${structure.bab_title}`;
    return structure.bab || '-';
  };

  return (
    <div className="container-fluid px-0">
      <div className="mb-4">
        <button
          className="btn btn-sm btn-outline-secondary mb-3"
          onClick={() => {
            if (saving || syncing) {
              Swal.fire('Mohon Tunggu', 'Proses penyimpanan/pengiriman sedang berjalan.', 'warning');
              return;
            }
            navigate('/dashboard/manage/document');
          }}
          disabled={saving || syncing}
        >
          <i className="fa fa-arrow-left me-2"></i>
          Kembali
        </button>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-dark" role="status"></div>
        </div>
      ) : (
        <div className="row g-3">
          <div className="col-12">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm h-100">
              <div className="mb-4">
                <div className="mb-1 flex items-center gap-2 text-slate-800">
                  <FileText size={18} />
                  <h4 className="mb-0 text-lg font-semibold">Teks Hasil Parse {document?.title || 'Memuat dokumen...'}</h4>
                </div>
                <div className="small text-muted">Edit bila LocalParse/OCR salah membaca struktur atau isi.</div>
              </div>
              <div className="d-flex flex-wrap align-items-center gap-2">
                {document ? getVerificationBadge(document) : null}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => saveVerification({ rebuildChunks: true })}
                  disabled={loading || saving || syncing}
                >
                  Buat ulang chunk dari teks
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setShowOcrPreviewModal(true)}
                  disabled={loading || saving || syncing}
                >
                  OCR Normalization Preview
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-dark"
                  onClick={() => saveVerification()}
                  disabled={loading || saving || syncing}
                >
                  {saving ? 'Menyimpan...' : 'Simpan verifikasi'}
                </button>
                <button
                  type="button"
                  className="btn btn-sm text-white"
                  style={{ backgroundColor: '#0d6efd', borderColor: '#0d6efd' }}
                  onClick={sendToQdrant}
                  disabled={
                    loading ||
                    saving ||
                    syncing ||
                    !document
                  }
                >
                  {syncing ? 'Mengirim...' : 'Kirim ke Qdrant'}
                </button>
              </div>
              <hr className="my-3" />
              <div>
                <div className="d-flex justify-content-start align-items-center mb-2">
                  <button
                    type="button"
                    className="btn btn-sm text-white"
                    style={{ backgroundColor: '#fd7e14', borderColor: '#fd7e14' }}
                    onClick={insertSeparatorIntoContent}
                    disabled={saving || syncing || chunks.length === 0}
                  >
                    Tambah Separator Chunk
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary ms-2"
                    onClick={() => setWrapEditor((value) => !value)}
                    disabled={saving || syncing}
                  >
                    {wrapEditor ? 'Tampilkan baris asli' : 'Bungkus baris panjang'}
                  </button>
                </div>
                <div
                  style={{
                    display: 'flex',
                    height: '68vh',
                    border: '1px solid #dee2e6',
                    borderRadius: '0.375rem',
                    overflow: 'hidden',
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    lineHeight: `${LINE_H}px`,
                  }}
                >
                  {/* Line number gutter */}
                  <div
                    ref={lineNumbersRef}
                    style={{
                      display: wrapEditor ? 'none' : 'block',
                      width: '48px',
                      flexShrink: 0,
                      overflowY: 'hidden',
                      overflowX: 'hidden',
                      backgroundColor: '#f8f9fa',
                      borderRight: '1px solid #dee2e6',
                      paddingTop: '8px',
                      paddingBottom: '8px',
                      userSelect: 'none',
                    }}
                  >
                    {renderLineNumbers()}
                  </div>
                  {/* Editor area: highlight layer + textarea stacked */}
                  <div style={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
                    {/* Background highlight layer for separator rows */}
                    <div
                      ref={highlightRef}
                      style={{
                        display: wrapEditor ? 'none' : 'block',
                        position: 'absolute',
                        top: 0, left: 0, right: 0, bottom: 0,
                        paddingTop: '8px',
                        overflowY: 'hidden',
                        overflowX: 'hidden',
                        pointerEvents: 'none',
                        zIndex: 0,
                      }}
                    >
                      {renderSeparatorHighlights()}
                    </div>
                    {/* Textarea — transparent background so highlight shows through */}
                    <textarea
                      ref={contentTextareaRef}
                      style={{
                        position: 'absolute',
                        top: 0, left: 0, right: 0, bottom: 0,
                        width: '100%',
                        height: '100%',
                        resize: 'none',
                        border: 'none',
                        outline: 'none',
                        padding: '8px',
                        fontFamily: 'monospace',
                        fontSize: '14px',
                        lineHeight: `${LINE_H}px`,
                        overflowY: 'auto',
                        overflowX: wrapEditor ? 'hidden' : 'auto',
                        whiteSpace: wrapEditor ? 'pre-wrap' : 'pre',
                        overflowWrap: wrapEditor ? 'anywhere' : 'normal',
                        color: '#212529',
                        backgroundColor: 'transparent',
                        zIndex: 1,
                      }}
                      wrap={wrapEditor ? 'soft' : 'off'}
                      value={content}
                      onChange={(event) => setContent(event.target.value)}
                      onScroll={handleEditorScroll}
                      disabled={saving || syncing}
                    />
                  </div>
                </div>
                <div className="small text-muted mt-2">
                  Mode bungkus baris menampilkan seluruh isi tanpa scroll horizontal. Gunakan baris asli untuk melihat nomor baris fisik.
                </div>
                <div className="small text-muted mt-1">
                  Format separator yang diterima: <span className="font-monospace">--- [CHUNK_SEPARATOR] TIPE: pasal | BAB: BAB I | JUDUL_BAB: KETENTUAN UMUM | PASAL: 1 | MASIH_BERLAKU: true ---</span>
                </div>
                <div className="small text-muted mt-1">
                  Model metadata yang dikenali: <span className="font-monospace">TIPE</span>, <span className="font-monospace">BAB</span>, <span className="font-monospace">JUDUL_BAB</span>, <span className="font-monospace">BAGIAN</span>, <span className="font-monospace">PARAGRAF</span>, dan <span className="font-monospace">PASAL</span>.
                </div>
              </div>
            </div>
          </div>

          {SHOW_CHUNK_DATA && (
          <div className="col-xl-7 d-none" aria-hidden="true">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm h-100">
              <div className="mb-4 d-flex flex-column flex-lg-row justify-content-between gap-3">
                <div>
                  <div className="mb-1 flex items-center gap-2 text-slate-800">
                    <Blocks size={18} />
                    <h4 className="mb-0 text-lg font-semibold">Chunk Data</h4>
                  </div>
                  <div className="small text-muted">
                    {chunks.length} chunk. Periksa batas BAB/Pasal sebelum ingest Qdrant.
                  </div>
                </div>
                <input
                  type="text"
                  className="form-control rounded-lg border-slate-300"
                  style={{ maxWidth: 320 }}
                  placeholder="Cari Pasal/BAB/isi chunk..."
                  value={chunkSearch}
                  onChange={(event) => {
                    setChunkSearch(event.target.value);
                    setChunkPage(1);
                  }}
                />
              </div>
              <div>
                {chunks.length === 0 ? (
                  <div className="text-center text-muted py-5">Belum ada chunk untuk dokumen ini.</div>
                ) : (
                  <div>
                    <table className="table table-hover align-middle border mb-0 bg-white">
                      <thead className="table-light position-sticky top-0" style={{ zIndex: 1 }}>
                        <tr>
                          <th>No</th>
                          <th>Chunk (BAB/Pasal)</th>
                          <th>Status Qdrant</th>
                          <th>Isi Teks</th>
                          <th>Aksi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredChunks.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="text-center text-muted py-4">
                              Tidak ada chunk yang sesuai pencarian.
                            </td>
                          </tr>
                        ) : (
                          paginatedChunks.map(({ chunk, index }) => (
                            <tr key={chunk.id || index} className={activeChunk === index ? 'table-primary' : ''}>
                              <td>{index + 1}</td>
                              <td>
                                <div className="fw-bold">{getChunkLabel(chunk, index)}</div>
                                <div className="small text-muted">{getChunkBab(chunk)}</div>
                              </td>
                              <td>
                                <span className={`badge ${chunk.qdrant_point_id ? 'bg-success' : 'bg-secondary'}`}>
                                  {chunk.qdrant_point_id ? 'Synced' : 'Draft'}
                                </span>
                              </td>
                              <td>
                                <p className="mb-0 text-truncate small text-muted" style={{ maxWidth: '250px' }}>
                                  {(chunk.verified_text || chunk.chunk_text || '').slice(0, 100)}...
                                </p>
                              </td>
                              <td>
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => openChunkModal(index)}
                                >
                                  Edit / Evaluasi
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
                {filteredChunks.length > 0 ? (
                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center gap-2 mt-3">
                    <div className="small text-muted">
                      Menampilkan {(safeChunkPage - 1) * CHUNKS_PER_PAGE + 1}–{Math.min(safeChunkPage * CHUNKS_PER_PAGE, filteredChunks.length)} dari {filteredChunks.length} chunk
                    </div>
                    <nav aria-label="Pagination chunk" className="d-flex align-items-center">
                      <div className="btn-group btn-group-sm shadow-sm" role="group" aria-label="Navigasi halaman chunk">
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={() => setChunkPage(Math.max(1, safeChunkPage - 1))}
                          disabled={safeChunkPage === 1}
                        >
                          Sebelumnya
                        </button>
                        {chunkPageItems.map((item) => typeof item === 'number' ? (
                          <button
                            key={item}
                            type="button"
                            className={`btn ${safeChunkPage === item ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setChunkPage(item)}
                            aria-current={safeChunkPage === item ? 'page' : undefined}
                          >
                            {item}
                          </button>
                        ) : (
                          <button key={item} type="button" className="btn btn-outline-primary" disabled aria-hidden="true">
                            …
                          </button>
                        ))}
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={() => setChunkPage(Math.min(totalChunkPages, safeChunkPage + 1))}
                          disabled={safeChunkPage === totalChunkPages}
                        >
                          Berikutnya
                        </button>
                      </div>
                    </nav>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          )}
        </div>
      )}

      {!loading && retrievalData ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm mt-3">
          <div className="d-flex flex-column flex-lg-row justify-content-between gap-3 mb-3">
            <div>
              <div className="mb-1 flex items-center gap-2 text-slate-800">
                <Blocks size={18} />
                <h4 className="mb-0 text-lg font-semibold">Retrieval Chunks — Audit Read-only</h4>
              </div>
              <div className="small text-muted">
                Turunan semantic leaf dari canonical chunks. Panel ini tidak menyimpan embedding dan tidak menulis ke Qdrant.
              </div>
            </div>
            <input
              type="text"
              className="form-control rounded-lg border-slate-300"
              style={{ maxWidth: 360 }}
              placeholder="Cari identity/Pasal/Ayat/Huruf/isi..."
              value={retrievalSearch}
              onChange={(event) => {
                setRetrievalSearch(event.target.value);
                setRetrievalPage(1);
              }}
            />
          </div>

          <div className="d-flex flex-wrap gap-2 mb-3">
            <span className="badge bg-dark">Canonical: {retrievalData.canonical_chunk_count}</span>
            <span className="badge bg-primary">Retrieval: {retrievalData.retrieval_chunk_count}</span>
            <span className="badge bg-success">Candidate: {retrievalData.summary?.embed_candidate_true || 0}</span>
            <span className="badge bg-secondary">Skip: {retrievalData.summary?.embed_candidate_false || 0}</span>
            <span className={`badge ${(retrievalData.validation?.errors?.length || 0) > 0 ? 'bg-danger' : 'bg-success'}`}>
              Error: {retrievalData.validation?.errors?.length || 0}
            </span>
            <span className="badge bg-warning text-dark">Warning: {retrievalData.validation?.warnings?.length || 0}</span>
          </div>

          <div className="table-responsive border rounded">
            <table className="table table-hover align-middle mb-0 bg-white">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 70 }}>Seq</th>
                  <th style={{ minWidth: 230 }}>Hierarchy</th>
                  <th style={{ width: 150 }}>Level</th>
                  <th style={{ width: 120 }}>Candidate</th>
                  <th style={{ minWidth: 360 }}>Content</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRetrievalChunks.length === 0 ? (
                  <tr><td colSpan="5" className="text-center text-muted py-4">Tidak ada retrieval chunk yang sesuai.</td></tr>
                ) : paginatedRetrievalChunks.map((chunk) => {
                  const hierarchy = [
                    chunk.bab ? `BAB ${chunk.bab}` : null,
                    chunk.pasal ? `Pasal ${chunk.pasal}` : null,
                    chunk.ayat ? `Ayat (${chunk.ayat})` : null,
                    chunk.huruf ? `Huruf ${chunk.huruf}` : null,
                    chunk.angka ? `Angka ${chunk.angka}` : null,
                  ].filter(Boolean).join(' › ') || chunk.section_type;
                  return (
                    <tr key={chunk.retrieval_identity}>
                      <td>{chunk.sequence}</td>
                      <td>
                        <div className="fw-semibold">{hierarchy}</div>
                        <div className="small text-muted font-monospace text-break">{chunk.retrieval_identity}</div>
                        <div className="small text-muted">Source: {chunk.source_legal_identity}</div>
                      </td>
                      <td>
                        <span className="badge bg-light text-dark border me-1">{chunk.section_type}</span>
                        <span className="badge bg-info text-dark">{chunk.chunk_level}</span>
                      </td>
                      <td>
                        <span className={`badge ${chunk.embed_candidate ? 'bg-success' : 'bg-secondary'}`}>
                          {chunk.embed_candidate ? 'Embed' : 'Skip'}
                        </span>
                        {chunk.skip_reason ? <div className="small text-muted mt-1">{chunk.skip_reason}</div> : null}
                      </td>
                      <td>
                        <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{chunk.content}</div>
                        {chunk.parent_context ? (
                          <details className="mt-2">
                            <summary className="small text-primary" style={{ cursor: 'pointer' }}>Parent context</summary>
                            <div className="small text-muted font-monospace mt-1" style={{ whiteSpace: 'pre-wrap' }}>
                              {chunk.parent_context}
                            </div>
                          </details>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center gap-2 mt-3">
            <div className="small text-muted">
              Menampilkan {filteredRetrievalChunks.length ? (safeRetrievalPage - 1) * RETRIEVAL_PER_PAGE + 1 : 0}–{Math.min(safeRetrievalPage * RETRIEVAL_PER_PAGE, filteredRetrievalChunks.length)} dari {filteredRetrievalChunks.length} retrieval chunk
            </div>
            <div className="btn-group btn-group-sm">
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => setRetrievalPage(Math.max(1, safeRetrievalPage - 1))}
                disabled={safeRetrievalPage === 1}
              >
                Sebelumnya
              </button>
              <button type="button" className="btn btn-outline-primary" disabled>
                {safeRetrievalPage} / {retrievalTotalPages}
              </button>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => setRetrievalPage(Math.min(retrievalTotalPages, safeRetrievalPage + 1))}
                disabled={safeRetrievalPage === retrievalTotalPages}
              >
                Berikutnya
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showOcrPreviewModal ? (
        <div className="modal d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-scrollable" style={{ maxWidth: '96vw', width: '96vw' }}>
            <div className="modal-content border-0 shadow" style={{ maxHeight: '92vh' }}>
              <div className="modal-header">
                <div>
                  <h5 className="modal-title mb-1">OCR Normalization Preview</h5>
                  <div className="small text-muted">
                    Panel ini menampilkan koreksi aman yang akan dipakai sebelum user menyimpan verifikasi final.
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowOcrPreviewModal(false)}
                  disabled={saving || syncing}
                ></button>
              </div>
              <div className="modal-body" style={{ overflowY: 'auto', overflowX: 'auto' }}>
                <div className="d-flex align-items-center gap-2 mb-3">
                  {ocrPreviewLoading ? <span className="badge bg-secondary">Mengecek...</span> : null}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-dark"
                    onClick={() => fetchOcrPreview(content)}
                    disabled={saving || syncing || loading}
                    title="Refresh preview normalisasi OCR"
                  >
                    Refresh Preview
                  </button>
                </div>

                {ocrPreviewError ? (
                  <div className="alert alert-warning mb-3">{ocrPreviewError}</div>
                ) : null}

                {!ocrPreview && !ocrPreviewLoading ? (
                  <div className="text-muted small">
                    Tulis atau ubah teks di panel utama untuk melihat preview perubahan OCR.
                  </div>
                ) : null}

                {ocrPreview ? (
                  <>
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      <span className="badge bg-dark">Mode: safe</span>
                      <span className="badge bg-info text-dark">
                        {ocrPreview.corrections?.length || 0} koreksi
                      </span>
                      <span className="badge bg-light text-dark border">
                        Raw {ocrPreview.content_raw?.length || 0} chars
                      </span>
                      <span className="badge bg-light text-dark border">
                        Clean {ocrPreview.content_clean?.length || 0} chars
                      </span>
                    </div>

                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label small text-muted">Raw</label>
                        <textarea
                          className="form-control font-monospace"
                          style={{ minHeight: 180, whiteSpace: 'pre-wrap' }}
                          value={ocrPreview.content_raw || ''}
                          readOnly
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small text-muted">Clean</label>
                        <textarea
                          className="form-control font-monospace"
                          style={{ minHeight: 180, whiteSpace: 'pre-wrap' }}
                          value={ocrPreview.content_clean || ''}
                          readOnly
                        />
                      </div>
                    </div>

                    <div className="mt-3">
                      <div className="fw-semibold mb-2">Daftar Koreksi</div>
                      {Array.isArray(ocrPreview.corrections) && ocrPreview.corrections.length > 0 ? (
                        <div className="border rounded bg-white" style={{ maxHeight: '42vh', overflow: 'auto' }}>
                          <table className="table table-sm align-middle mb-0">
                            <thead className="table-light position-sticky top-0" style={{ zIndex: 1 }}>
                              <tr>
                                <th style={{ minWidth: 160 }}>Original</th>
                                <th style={{ minWidth: 160 }}>Suggestion</th>
                                <th style={{ width: 120 }}>Type</th>
                                <th style={{ width: 100 }}>Confidence</th>
                                <th style={{ minWidth: 240 }}>Reason</th>
                              </tr>
                            </thead>
                            <tbody>
                              {ocrPreview.corrections.map((item, index) => (
                                <tr key={`${item.original}-${index}`}>
                                  <td className="font-monospace">{item.original}</td>
                                  <td className="font-monospace">{item.suggestion}</td>
                                  <td>
                                    <span className={`badge ${item.type === 'auto_fix' ? 'bg-success' : 'bg-warning text-dark'}`}>
                                      {item.type}
                                    </span>
                                  </td>
                                  <td>{Number(item.confidence || 0).toFixed(2)}</td>
                                  <td>{item.reason}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-muted small">Tidak ada koreksi yang perlu ditampilkan.</div>
                      )}
                    </div>
                  </>
                ) : null}
              </div>
              <div className="modal-footer bg-white" style={{ borderTop: '1px solid rgba(0, 0, 0, 0.08)' }}>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowOcrPreviewModal(false)}
                  disabled={saving || syncing}
                >
                  Tutup
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {showChunkModal && selectedChunk ? (
        <div className="modal d-block" tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" style={{ maxWidth: 'calc(100vw - 2rem)', margin: '1rem auto' }}>
            <div className="modal-content border-0 shadow" style={{ maxHeight: 'calc(100vh - 2rem)' }}>
              <div className="modal-header">
                <div>
                  <h5 className="modal-title mb-1">
                    {getChunkLabel(selectedChunk, activeChunk)}
                  </h5>
                  <div className="small text-muted">
                    Chunk {activeChunk + 1} • Token estimasi: {selectedChunk.token_count || 0}
                    {selectedChunk.qdrant_point_id ? ` • Qdrant: ${selectedChunk.qdrant_point_id}` : ''}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowChunkModal(false)}
                  disabled={saving || syncing}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row g-3 mb-3">
                  <div className="col-md-4">
                    <label className="form-label">BAB</label>
                    <input className="form-control" value={getChunkBab(selectedChunk)} disabled />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Pasal/Chunk</label>
                    <input className="form-control" value={getChunkLabel(selectedChunk, activeChunk)} disabled />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Status Qdrant</label>
                    <input className="form-control" value={selectedChunk.qdrant_point_id ? 'Synced' : 'Draft'} disabled />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Status Berlaku</label>
                    <select
                      className="form-select"
                      value={selectedChunk.masih_berlaku === false ? 'false' : 'true'}
                      onChange={(event) => updateChunkValidity(activeChunk, event.target.value === 'true')}
                      disabled={saving || syncing}
                    >
                      <option value="true">Masih berlaku</option>
                      <option value="false">Tidak berlaku</option>
                    </select>
                  </div>
                </div>
                <label className="form-label">Isi Chunk Terverifikasi</label>
                <textarea
                  className="form-control font-monospace"
                  style={{ minHeight: '18rem', maxHeight: 'calc(100vh - 22rem)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                  value={selectedChunk.verified_text ?? selectedChunk.chunk_text ?? ''}
                  onChange={(event) => updateChunkVerifiedText(activeChunk, event.target.value)}
                  disabled={saving || syncing}
                />
                <div className="small text-muted mt-2">
                  Perubahan disimpan ke kolom verified_text setelah menekan tombol Simpan perubahan.
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowChunkModal(false)}
                  disabled={saving || syncing}
                >
                  Batal / Tutup
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setShowChunkModal(false);
                    saveVerification();
                  }}
                  disabled={saving || syncing}
                >
                  Simpan perubahan
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default DokumenChunkVerificationPage;
